// src/admin/AttendanceAssignment.js
import React, { useContext, useState, useMemo } from 'react';
import { StudentContext } from '../StudentContext';
import { updateChurchAttendanceAccess, updateCatechistAttendanceAccess } from '../FirebaseService';
import './AttendanceAssignment.css';

const AttendanceAssignment = () => {
  const { catechistsData, updateLocalCatechist } = useContext(StudentContext);
  const [loadingState, setLoadingState] = useState({});
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // 1. Kiểm tra quyền thao tác (Chỉ Admin hoặc Trưởng Ban Kỷ Luật mới được phân quyền)
  const userRole = localStorage.getItem('userRole') || 'catechist';
  const rawDept = localStorage.getItem('departmentLeader') || '';
  const isDisciplineLeader = useMemo(() => {
    let depts = rawDept;
    try {
      if (typeof rawDept === 'string' && (rawDept.startsWith('[') || rawDept.startsWith('{'))) {
        depts = JSON.parse(rawDept);
      }
    } catch (e) {}
    return Array.isArray(depts)
      ? depts.includes('Ban Kỷ Luật')
      : typeof depts === 'string' && depts.split(',').map(s => s.trim()).includes('Ban Kỷ Luật');
  }, [rawDept]);

  const canManageAssignment = userRole === 'admin' || isDisciplineLeader;

  // 2. Bật/Tắt quyền điểm danh Thiếu Nhi (Đi Lễ)
  const handleToggleChurchAccess = async (catechist) => {
    if (!canManageAssignment) return;
    const newAccess = !(catechist.churchAttendanceAccess ?? true);
    const key = `${catechist.uid}-church`;
    setLoadingState(prev => ({ ...prev, [key]: true }));
    setError('');

    try {
      await updateChurchAttendanceAccess(catechist.uid, newAccess);
      updateLocalCatechist(catechist.uid, { churchAttendanceAccess: newAccess });
    } catch (err) {
      setError('Lỗi khi cập nhật quyền điểm danh thiếu nhi: ' + err.message);
    } finally {
      setLoadingState(prev => ({ ...prev, [key]: false }));
    }
  };

  // 3. Bật/Tắt quyền điểm danh Giáo Lý Viên
  const handleToggleCatechistAccess = async (catechist) => {
    if (!canManageAssignment) return;
    const newAccess = !(catechist.catechistAttendanceAccess ?? false);
    const key = `${catechist.uid}-catechist`;
    setLoadingState(prev => ({ ...prev, [key]: true }));
    setError('');

    try {
      await updateCatechistAttendanceAccess(catechist.uid, newAccess);
      updateLocalCatechist(catechist.uid, { catechistAttendanceAccess: newAccess });
    } catch (err) {
      setError('Lỗi khi cập nhật quyền điểm danh GLV: ' + err.message);
    } finally {
      setLoadingState(prev => ({ ...prev, [key]: false }));
    }
  };

  const filteredCatechists = useMemo(() => {
    return (catechistsData || [])
      .filter(c => c.status !== 'inactive')
      .filter(c => `${c.holyName || ''} ${c.fullName || ''}`.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [catechistsData, searchTerm]);

  return (
    <div className="aa-container">
      <div className="aa-header">
        <h2 className="aa-title">Phân công điểm danh</h2>
        <p className="aa-subtitle">Quản lý quyền điểm danh Thiếu Nhi và Giáo Lý Viên</p>
      </div>

      {error && <p className="aa-error-msg">{error}</p>}

      <input
        type="text"
        placeholder="🔎 Tìm kiếm theo tên giáo lý viên..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="aa-search-input"
      />

      <div className="aa-table-wrapper">
        <table className="aa-table">
          <thead>
            <tr>
              <th>Tên Thánh & Họ Tên</th>
              <th>Lớp</th>
              <th className="aa-th-center">Điểm danh Thiếu Nhi Đi Lễ</th>
              <th className="aa-th-center">Điểm danh GLV</th>
            </tr>
          </thead>
          <tbody>
            {filteredCatechists.map((catechist, index) => {
              // Kiểm tra xem giáo lý viên này có phải là Admin hoặc Ban Kỷ Luật không
              let depts = catechist.departmentLeader || '';
              try {
                if (typeof depts === 'string' && (depts.startsWith('[') || depts.startsWith('{'))) {
                  depts = JSON.parse(depts);
                }
              } catch (e) {}
              const isDiscipline = Array.isArray(depts)
                ? depts.includes('Ban Kỷ Luật')
                : typeof depts === 'string' && depts.split(',').map(s => s.trim()).includes('Ban Kỷ Luật');

              const isAdminOrDiscipline = catechist.role === 'admin' || isDiscipline;

              const churchAccess = catechist.churchAttendanceAccess ?? true;
              const catechistAccess = catechist.catechistAttendanceAccess ?? false;

              const isChurchLoading = Boolean(loadingState[`${catechist.uid}-church`]);
              const isCatLoading = Boolean(loadingState[`${catechist.uid}-catechist`]);

              return (
                <tr key={catechist.uid} className={index % 2 === 0 ? '' : 'aa-tr-even'}>
                  <td className="aa-td-name">{catechist.holyName} {catechist.fullName}</td>
                  <td className="aa-td-class">{catechist.className || 'N/A'}</td>

                  {/* CỘT 1: ĐIỂM DANH THIẾU NHI ĐI LỄ */}
                  <td className="aa-td-center">
                    {isAdminOrDiscipline ? (
                      <span className="aa-badge-admin">Luôn có quyền</span>
                    ) : (
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={churchAccess}
                          onChange={() => handleToggleChurchAccess(catechist)}
                          disabled={!canManageAssignment || isChurchLoading}
                        />
                        <span className="slider"></span>
                      </label>
                    )}
                  </td>

                  {/* CỘT 2: ĐIỂM DANH GIÁO LÝ VIÊN */}
                  <td className="aa-td-center">
                    {isAdminOrDiscipline ? (
                      <span className="aa-badge-admin">Luôn có quyền</span>
                    ) : (
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={catechistAccess}
                          onChange={() => handleToggleCatechistAccess(catechist)}
                          disabled={!canManageAssignment || isCatLoading}
                        />
                        <span className="slider"></span>
                      </label>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceAssignment;